<script setup>
import { ref, onMounted, watch } from 'vue';
import AppNavbar from './components/AppNavbar.vue';
import AppSidebar from './components/AppSidebar.vue';
import LoadingScreen from './components/LoadingScreen.vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const isSidebarCollapsed = ref(false);
const isMobileView = ref(false);
const isLoading = ref(true);

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
};

const closeSidebar = () => {
  if (isMobileView.value) {
    isSidebarCollapsed.value = true;
  }
};

const updateMobileView = () => {
  isMobileView.value = window.innerWidth < 768;
  if (isMobileView.value) {
    isSidebarCollapsed.value = true;
  }
};

onMounted(() => {
  updateMobileView();
  window.addEventListener('resize', updateMobileView);
  router.afterEach(() => {
    closeSidebar();
  });
  
  // Simulate loading time
  setTimeout(() => {
    isLoading.value = false;
  }, 1500);
});

watch(isSidebarCollapsed, (collapsed) => {
  if (isMobileView.value) {
    document.body.style.overflow = collapsed ? 'auto' : 'hidden';
  }
});
</script>

<template>
  <LoadingScreen :loading="isLoading" />
  
  <div class="app-container" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
    <AppNavbar @toggle-sidebar="toggleSidebar" :collapsed="isSidebarCollapsed" />
    <AppSidebar :collapsed="isSidebarCollapsed" />
    <div 
      v-if="!isSidebarCollapsed && isMobileView" 
      class="sidebar-overlay"
      @click="toggleSidebar"
    ></div>
    <main class="main-content">
      <div class="content-wrapper">
        <router-view v-if="!isLoading" :key="$route.fullPath"/>
      </div>
    </main>
  </div>
</template>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow-x: hidden;
  transition: all 0.3s ease;
  padding-left: 70px;
}

.app-container.sidebar-collapsed {
  padding-left: 0;
}

.main-content {
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 72px);
  width: 100%;
  margin-top: 72px;
}

.content-wrapper {
  padding: 0;
  flex-grow: 1;
  overflow-y: auto;
}

.sidebar-overlay {
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1020;
  backdrop-filter: blur(2px);
}

@media (max-width: 768px) {
  .app-container {
    padding-left: 0;
  }

  .main-content {
    margin-left: 0;
    margin-top: 60px;
    min-height: calc(100vh - 60px);
  }

  .sidebar-overlay {
    top: 60px;
  }
}

@media (max-width: 480px) {
  .app-container {
    padding-left: 0;
  }
}
</style>